import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, Loader2, Sparkles } from "lucide-react";

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";

function Row({ kind, refId, lang, name, entry, canApprove, onChange }) {
  const [text, setText] = useState(entry?.text || "");
  const [busy, setBusy] = useState(null);
  const run = async (k, fn, msg) => {
    setBusy(k);
    try { const { data } = await fn(); const next = data.translations || {}; setText(next[lang]?.text || text); onChange(next); toast.success(msg); }
    catch (e) { toast.error(err(e)); } finally { setBusy(null); }
  };
  const state = !entry ? "Not translated" : entry.stale ? "Out of date" : entry.approved ? "Approved · prints" : "Draft · needs approval";
  const base = { kind, ref: refId, lang };
  return (
    <div className="rounded-lg border border-slate-200 p-2.5" data-testid={`lt-${kind}-${lang}`}>
      <div className="mb-1.5 flex items-center text-xs"><b className="text-slate-800">{name}</b><span className={`ml-auto rounded-full px-2 py-0.5 font-semibold ${entry?.approved && !entry?.stale ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"}`} data-testid={`lt-${kind}-${lang}-state`}>{state}</span></div>
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={`${name} version`} data-testid={`lt-${kind}-${lang}-text`} />
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("ai", () => api.post("/welcome-label/label-text/translate", { ...base, langs: [lang] }), "AI draft ready — please review")} data-testid={`lt-${kind}-${lang}-ai`}>{busy === "ai" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}AI draft</Button>
        <Button size="sm" variant="outline" disabled={!!busy || text.trim().length < 3 || text === entry?.text} onClick={() => run("save", () => api.put("/welcome-label/label-text/translation", { ...base, text }), "Saved as draft")} data-testid={`lt-${kind}-${lang}-save`}>Save edit</Button>
        {canApprove && entry && (!entry.approved || entry.stale) && <Button size="sm" disabled={!!busy || text !== entry.text} onClick={() => run("ok", () => api.post("/welcome-label/label-text/approve", base), `${name} approved`)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`lt-${kind}-${lang}-approve`}><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>}
      </div>
    </div>
  );
}

export function TranslationsPanel({ kind, refId, translations, languages, canApprove, onUpdated }) {
  const [tr, setTr] = useState(translations || {});
  const update = (next) => { setTr(next); onUpdated?.(next); };
  return (
    <div className="grid gap-2 md:grid-cols-2" data-testid={`lt-panel-${kind}`}>
      {Object.entries(languages).map(([l, n]) => <Row key={`${l}-${tr[l]?.drafted_at || ""}`} kind={kind} refId={refId} lang={l} name={n} entry={tr[l]} canApprove={canApprove} onChange={update} />)}
    </div>
  );
}
